import { AlertCircle, LoaderCircle, RefreshCw, ShoppingBag } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import EmptyState from "../components/EmptyState";
import OrderCard from "../orders/OrderCard";
import OrderSummaryCards from "../orders/OrderSummaryCards";
import OrderToolbar from "../orders/OrderToolbar";
import { fetchOrderSummary, fetchOrders } from "../orders/ordersApi";

const emptyFilters = { search: "", status: "", payment_status: "", fulfillment_status: "", delivery_status: "", priority: "", requested_delivery_date: "", ordering: "-placed_at" };

export default function AdminOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(filters.search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  const apiFilters = useMemo(() => ({ ...filters, search: debouncedSearch }), [filters, debouncedSearch]);

  const loadData = useCallback(async (signal) => {
    setIsLoading(true);
    setError("");
    try {
      const [rows, summaryData] = await Promise.all([fetchOrders({ ...apiFilters, signal }), fetchOrderSummary({ ...apiFilters, signal })]);
      setOrders(rows);
      setSummary(summaryData);
    } catch (requestError) {
      if (requestError.name !== "AbortError") setError(requestError.message || "Orders could not be loaded.");
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [apiFilters]);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  function setFilter(field, value) {
    setFilters((current) => ({ ...current, [field]: value }));
  }

  return (
    <AdminLayout>
      <section className="admin-page orders-page">
        <header className="admin-page-heading">
          <div>
            <span className="admin-eyebrow">Order Management</span>
            <h1>Orders</h1>
            <p>Track checkout orders from review through payment, preparation, delivery, refunds, notes, and activity.</p>
          </div>
          <button type="button" className="btn btn--secondary" onClick={() => loadData()}>
            <RefreshCw size={17} /> Refresh
          </button>
        </header>

        {error && <div className="admin-menu-error"><AlertCircle size={18} /><span>{error}</span></div>}
        <OrderSummaryCards summary={summary} />
        <OrderToolbar filters={filters} onChange={setFilter} onClear={() => setFilters(emptyFilters)} />

        {isLoading ? (
          <div className="inventory-loading"><LoaderCircle size={22} className="admin-spin" /> Loading orders...</div>
        ) : orders.length ? (
          <div className="order-card-grid">
            {orders.map((order) => <OrderCard key={order.id} order={order} onOpen={(row) => navigate(`/admin/orders/${row.id}`)} />)}
          </div>
        ) : (
          <EmptyState icon={ShoppingBag} title="No orders yet" description="Checkout orders will appear here after the customer ordering flow creates them." />
        )}
      </section>
    </AdminLayout>
  );
}
